import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/auth.css';

const EMOJIS = ['🎤', '🎵', '🎶', '🎸', '🎹', '🎺', '🎻', '🥁', '🎙️', '🎼', '🎧', '🌟', '⭐', '🦋', '🌈'];

function getPasswordStrength(pw) {
  if (pw.length < 6) return { level: 0, label: 'Too short', cls: '' };
  if (pw.length < 8) return { level: 1, label: 'Weak', cls: 'strength-weak' };
  if (!/(?=.*[A-Z])(?=.*[0-9])/.test(pw)) return { level: 2, label: 'Fair', cls: 'strength-fair' };
  if (pw.length < 12) return { level: 3, label: 'Good', cls: 'strength-good' };
  return { level: 4, label: 'Strong', cls: 'strength-strong' };
}

function MockCaptcha({ onVerify }) {
  const [verified, setVerified] = useState(false);
  const handleVerify = () => { setVerified(true); onVerify('dev-bypass'); };
  return (
    <div className="captcha-box">
      {verified ? (
        <div className="captcha-verified">✅ Verified</div>
      ) : (
        <button type="button" className="captcha-btn" onClick={handleVerify}>
          <span className="captcha-check">☐</span>
          I'm not a robot
        </button>
      )}
      <div className="captcha-logo">🔒 hCaptcha</div>
    </div>
  );
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    displayName: '',
    emoji: '🎤',
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const strength = getPasswordStrength(form.password);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) { setError('Please complete the captcha'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }

    setLoading(true);
    setError('');

    try {
      await register({ ...form, captchaToken });
      setSuccess('Account created! Please check your email to verify your account.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page page-enter">
      <div className="auth-glow" />

      <div className="auth-card" style={{ maxWidth: 480 }}>
        <div className="auth-header">
          <div className="auth-icon animate-float">{form.emoji}</div>
          <h1 className="auth-title">Join the Stage</h1>
          <p className="auth-subtitle">Create your free singer profile</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Emoji Picker */}
          <div className="form-group">
            <label className="form-label">Your Emoji 🎭</label>
            <div className="emoji-picker">
              {EMOJIS.map(em => (
                <button
                  key={em}
                  type="button"
                  className={`emoji-option ${form.emoji === em ? 'selected' : ''}`}
                  onClick={() => setForm({ ...form, emoji: em })}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="form-group flex-1">
              <label className="form-label">Display Name</label>
              <input
                type="text"
                name="displayName"
                className="form-input"
                placeholder="Star Singer"
                value={form.displayName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group flex-1">
              <label className="form-label">Username *</label>
              <input
                type="text"
                name="username"
                className="form-input"
                placeholder="star_singer"
                value={form.username}
                onChange={handleChange}
                required
                pattern="[a-zA-Z0-9_-]+"
                minLength={3}
                maxLength={50}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="form-input"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password *</label>
            <input
              type="password"
              name="password"
              className="form-input"
              placeholder="Min. 8 characters"
              value={form.password}
              onChange={handleChange}
              required
              minLength={8}
            />
            {form.password && (
              <div className="password-strength">
                <div className={`strength-bar ${strength.cls}`} />
                <div className="strength-label">{strength.label}</div>
              </div>
            )}
          </div>

          <MockCaptcha onVerify={setCaptchaToken} />

          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 20 }}
            disabled={loading}>
            {loading ? '⏳ Creating account...' : '🎤 Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p className="text-secondary text-sm">
            Already have an account?{' '}
            <Link to="/login" className="auth-link">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="auth-notes">
        {['♪', '♫', '♩', '♬'].map((note, i) => (
          <span key={i} className={`auth-note delay-${(i + 1) * 100}`}>{note}</span>
        ))}
      </div>
    </div>
  );
}
